import { useApolloClient } from '@apollo/client';
import type { UpdateOrganisationTabPreferencesMutationVariables } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { evictField } from '@/utils/graphqlUtils';

export type UpdateOrganisationTabPreferencesTRPCInput =
  UpdateOrganisationTabPreferencesMutationVariables;

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 * (insert_organisation_tab_preference / delete_user_tab_preference each
 * returning `{ affected_rows }`). The legacy GraphQL mutation performs
 * a two-phase write (upsert the org-level preference, delete any
 * user-level overrides for the same ObjectType); the tRPC equivalent
 * collapses both into a single service call so we synthesise the
 * response shape here.
 */
const mapTrpcResponseToGraphQL = () => ({
  insert_organisation_tab_preference: {
    __typename: 'organisation_tab_preference_mutation_response' as const,
    affected_rows: 1,
  },
  delete_user_tab_preference: {
    __typename: 'user_tab_preference_mutation_response' as const,
    affected_rows: 0,
  },
});

export const useUpdateOrganisationTabPreferencesTRPC = () => {
  const trpc = useTRPC();
  const apolloClient = useApolloClient();

  const mutation = useMutation({
    ...trpc.frontend.userTabPreference.updateOrganisationTabPreferences.mutationOptions(
      {
        onSuccess: () => {
          // The Apollo `useTabPreferences` reader still queries both
          // `organisation_tab_preference` and `user_tab_preference`
          // directly — keep the Apollo evictions in lockstep with the
          // old GraphQL mutation's `update` callback so existing
          // readers refresh. The two-phase mutation touches BOTH
          // tables, so both fields must be evicted.
          evictField(apolloClient.cache, 'organisation_tab_preference');
          evictField(apolloClient.cache, 'user_tab_preference');
        },
      }
    ),
  });

  return {
    updateOrganisationTabPreferences: async (
      variables: UpdateOrganisationTabPreferencesTRPCInput
    ) => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
